import React from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  successTitle: {
    color: "#4caf50",
  },
  errorTitle: {
    color: "#f44336",
  },
  warningTitle: {
    color: "#ff9800",
  },
  infoTitle: {
    color: "#2196f3",
  },
  statBox: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
    textAlign: "center",
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: theme.palette.text.primary,
  },
  successValue: {
    color: "#4caf50",
  },
  errorValue: {
    color: "#f44336",
  },
  messageBox: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  successMessage: {
    backgroundColor: "rgba(76, 175, 80, 0.1)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
  },
  errorMessage: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    border: "1px solid rgba(244, 67, 54, 0.3)",
  },
  warningMessage: {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
    border: "1px solid rgba(255, 152, 0, 0.3)",
  },
  infoMessage: {
    backgroundColor: "rgba(33, 150, 243, 0.1)",
    border: "1px solid rgba(33, 150, 243, 0.3)",
  },
  detailSection: {
    marginTop: theme.spacing(2),
  },
  detailItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0),
    fontSize: "0.875rem",
  },
}));

const ImportContactsResultModal = ({ open, onClose, result }) => {
  const classes = useStyles();

  if (!result) return null;

  const { 
    success, 
    message, 
    totalContacts = 0,
    successCount = 0,
    errorCount = 0,
    skippedCount = 0,
    lidCapturedCount = 0,
    contactsWithLid = 0,
    contactsWithJid = 0,
    contactsWithName = 0,
    contactsWithoutName = 0,
    contactsFromGroups = 0,
    whatsappId
  } = result;

  // Determinar el tipo de resultado
  const getResultType = () => {
    if (success && successCount > 0) return "success";
    if (errorCount > 0) return "error";
    if (totalContacts === 0) return "warning";
    return "info";
  };

  const resultType = getResultType();

  const getTitleIcon = () => {
    switch (resultType) {
      case "success":
        return <CheckCircleIcon className={classes.successTitle} />;
      case "error":
        return <ErrorIcon className={classes.errorTitle} />;
      case "warning":
        return <WarningIcon className={classes.warningTitle} />;
      default:
        return <InfoIcon className={classes.infoTitle} />;
    }
  };

  const getTitleClass = () => {
    switch (resultType) {
      case "success":
        return classes.successTitle;
      case "error":
        return classes.errorTitle;
      case "warning":
        return classes.warningTitle;
      default:
        return classes.infoTitle;
    }
  };

  const getMessageClass = () => {
    switch (resultType) {
      case "success":
        return classes.successMessage;
      case "error":
        return classes.errorMessage;
      case "warning":
        return classes.warningMessage;
      default:
        return classes.infoMessage;
    }
  };

  const getTitle = () => {
    if (success && successCount > 0) {
      return "✅ Importación Exitosa";
    }
    if (errorCount > 0) {
      return "❌ Error en la Importación";
    }
    if (totalContacts === 0) {
      return "⚠️ No Hay Contactos Disponibles";
    }
    return "ℹ️ Resultado de la Importación";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="import-result-dialog"
    >
      <DialogTitle id="import-result-dialog" className={classes.dialogTitle}>
        {getTitleIcon()}
        <span className={getTitleClass()}>{getTitle()}</span>
      </DialogTitle>
      <DialogContent dividers>
        {/* Mensaje principal */}
        <Box className={`${classes.messageBox} ${getMessageClass()}`}>
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            {message}
          </Typography>
        </Box>

        {/* Estadísticas principales */}
        {totalContacts > 0 && (
          <>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box className={classes.statBox}>
                  <Typography className={classes.statLabel}>Total Procesados</Typography>
                  <Typography className={classes.statValue}>{totalContacts}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box className={classes.statBox}>
                  <Typography className={classes.statLabel}>Importados Exitosamente</Typography>
                  <Typography className={`${classes.statValue} ${classes.successValue}`}>
                    {successCount}
                  </Typography>
                </Box>
              </Grid>
              {errorCount > 0 && (
                <Grid item xs={6}>
                  <Box className={classes.statBox}>
                    <Typography className={classes.statLabel}>Errores</Typography>
                    <Typography className={`${classes.statValue} ${classes.errorValue}`}>
                      {errorCount}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {skippedCount > 0 && (
                <Grid item xs={6}>
                  <Box className={classes.statBox}>
                    <Typography className={classes.statLabel}>Omitidos (Grupos)</Typography>
                    <Typography className={classes.statValue}>{skippedCount}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Detalles adicionales */}
        {totalContacts > 0 && (
          <Box className={classes.detailSection}>
            <Divider style={{ marginBottom: 12 }} />
            <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
              <span role="img" aria-label="Gráfico de barras">📊</span> Análisis Detallado:
            </Typography>
            {contactsWithLid > 0 && (
              <Box className={classes.detailItem}>
                <Typography variant="body2">Contactos con LID:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {contactsWithLid}
                </Typography>
              </Box>
            )}
            {lidCapturedCount > 0 && (
              <Box className={classes.detailItem}>
                <Typography variant="body2">LIDs capturados:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: "#4caf50" }}>
                  {lidCapturedCount}
                </Typography>
              </Box>
            )}
            {contactsWithJid > 0 && (
              <Box className={classes.detailItem}>
                <Typography variant="body2">Contactos con JID:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {contactsWithJid}
                </Typography>
              </Box>
            )}
            {contactsWithName > 0 && (
              <Box className={classes.detailItem}>
                <Typography variant="body2">Con nombre:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {contactsWithName}
                </Typography>
              </Box>
            )}
            {contactsWithoutName > 0 && (
              <Box className={classes.detailItem}>
                <Typography variant="body2">Sin nombre:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {contactsWithoutName}
                </Typography>
              </Box>
            )}
            {contactsFromGroups > 0 && (
              <Box className={classes.detailItem}>
                <Typography variant="body2">Probablemente de grupos:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {contactsFromGroups}
                </Typography>
              </Box>
            )}
            {whatsappId && (
              <Box className={classes.detailItem}>
                <Typography variant="body2">WhatsApp ID usado:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {whatsappId}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Mensaje de ayuda si no hay contactos */}
        {totalContacts === 0 && (
          <Box className={classes.detailSection}>
            <Divider style={{ marginBottom: 12 }} />
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              <strong>Posibles causas:</strong>
            </Typography>
            <Typography variant="body2" component="div" style={{ paddingLeft: 16 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>La tabla Baileys no tiene datos de contactos (WhatsApp aún no sincronizó)</li>
                <li>El campo contacts en Baileys es NULL o vacío</li>
                <li>El evento contacts.upsert de Baileys aún no se ha disparado</li>
              </ul>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 12, fontStyle: "italic" }}>
              <span role="img" aria-label="Bombilla de idea">💡</span> <strong>Recomendación:</strong> Espera unos minutos después de conectar WhatsApp. 
              Los contactos se sincronizan automáticamente cuando Baileys emite el evento contacts.upsert.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained" autoFocus>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportContactsResultModal;

